<template>
  <b-container>
    <b-alert
      :show="status.isResponseError"
      variant="danger"
      fade
      :dismissible="true"
    >
      <a
        href="#"
        class="alert-link"
      >請求錯誤，請重新送出資料。</a>
    </b-alert>
    <b-row>
      <b-col
        cols="12"
        sm="6"
        lg="3"
      >
        <BaseImageInput
          header="花梗影像"
          class="mb-3"
          @loaded="setFormImagePedicel"
        />
      </b-col>
      <b-col
        cols="12"
        sm="6"
        lg="3"
      >
        <BaseImageInput
          header="葉片影像"
          @loaded="setFormImageLeaf"
        />
      </b-col>
      <b-col
        cols="12"
        sm="6"
        lg="3"
      >
        <b-card
          header="選擇模型"
          class="mb-3"
        >
          <b-card-group class="d-block">
            <b-form @submit.prevent="calculate">
              <b-form-group class="d-block">
                <multiselect
                  v-model="form.model"
                  :options="yoloModelOptions"
                  :close-on-select="true"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :show-labels="false"
                  placeholder="請選擇合適的模型"
                  label="groupLabel"
                  track-by="groupLabel"
                >
                  <template
                    slot="singleLabel"
                    slot-scope="props"
                  >
                    <span>
                      <span>{{ props.option.groupLabel }}</span>
                    </span>
                  </template>
                  <template
                    slot="option"
                    slot-scope="props"
                  >
                    <div>
                      <p>{{ props.option.groupItemLabel }}</p>
                      <div
                        v-for="item in props.option.groupValues"
                        :key="item.id"
                        class="d-block"
                      >
                        {{ item.name }}
                      </div>
                    </div>
                  </template>
                  <span slot="noResult">Oops! No elements found. Consider changing the search query.</span>
                </multiselect>
              </b-form-group>
              <b-form-group class="d-block m-0">
                <b-btn
                  type="submit"
                  variant="primary"
                  :disabled="status.calculating || status.sendingResult"
                >
                  計算結果
                </b-btn>
              </b-form-group>
            </b-form>
          </b-card-group>
        </b-card>
        <b-card class="mb-3">
          <b-list-group>
            <b-list-group-item>
              <BaseListItem
                label="花朵開放程度："
                :loading="status.isDetectorResultLoad"
                :modified="status.resultModified"
                :original-value="bloomingDegree || '--'"
                :correct-value="formResultModify.correctBloomingDegree"
              />
            </b-list-group-item>
            <b-list-group-item>
              <BaseListItem
                label="總花朵數："
                :loading="status.isDetectorResultLoad"
                :modified="status.resultModified"
                :original-value="allFlowerCountResult"
                :correct-value="formResultModify.correctFlower"
              />
            </b-list-group-item>
            <b-list-group-item>
              <BaseListItem
                label="花梗數："
                :loading="status.isDetectorResultLoad"
                :format-result="formatResult"
                :modified="status.resultModified"
                :original-value="detectorCountResult['pedicel']"
                :correct-value="formResultModify.correctPedicel"
              />
            </b-list-group-item>
            <b-list-group-item>
              <b-form-group
                label-cols-sm="5"
                label-cols-lg="4"
                content-cols-sm
                content-cols-lg
                label="株高："
              >
                <b-form-input type="number" min="0" step="0.1" v-model="result.height"></b-form-input>
              </b-form-group>
            </b-list-group-item>
          </b-list-group>
        </b-card>
      </b-col>

      <b-col
        cols="12"
        sm="6"
        lg="3"
      >
        <b-card
          class="mb-3"
          header="綜合評判結果"
        >
          <b-card-group>
            <b-card-title>
              綜合結果:
              <span v-show="!status.isDetectorResultLoad">{{ calculationGrade | formatResultGrade }}</span>
              <span
                class="text-success d-none"
                :class="{'d-inline-block': status.resultModified}"
              >
                ( {{ formResultModify.correctResult }} )
              </span>
            </b-card-title>
            <b-spinner
              v-show="status.isDetectorResultLoad"
              variant="success"
              class="ml-2"
              label="Spinning"
            />
          </b-card-group>
          <b-card-group class="d-block">
            <b-list-group>
              <b-list-group-item>
                <BaseListItem
                  label="花朵受傷："
                  :loading="status.isDetectorResultLoad"
                  :format-result="formatResult"
                  :modified="status.resultModified"
                  :original-value="detectorCountResult[OBJECT_CLASS_NAME.FLOWER_INJURED]"
                  :correct-value="formResultModify.correctFlowerInjured"
                />
              </b-list-group-item>
              <b-list-group-item>
                <BaseListItem
                  label="花朵凋萎（黃化）："
                  :loading="status.isDetectorResultLoad"
                  :format-result="formatResult"
                  :modified="status.resultModified"
                  :original-value="detectorCountResult[OBJECT_CLASS_NAME.FLOWER_YELLOW]"
                  :correct-value="formResultModify.correctFlowerYellow"
                />
              </b-list-group-item>
              <b-list-group-item>
                <BaseListItem
                  label="花苞黃化（消蕾）："
                  :loading="status.isDetectorResultLoad"
                  :format-result="formatResult"
                  :modified="status.resultModified"
                  :original-value="detectorCountResult[OBJECT_CLASS_NAME.FLOWER_BUD_YELLOW]"
                  :correct-value="formResultModify.correctBudYellow"
                />
              </b-list-group-item>
              <b-list-group-item>
                <BaseListItem
                  label="掉花（苞）："
                  :loading="status.isDetectorResultLoad"
                  :format-result="formatResult"
                  :modified="status.resultModified"
                  :original-value="detectorCountResult[OBJECT_CLASS_NAME.FLOWER_BUD_DROP]"
                  :correct-value="formResultModify.correctDropFlower"
                />
              </b-list-group-item>
              <b-list-group-item>
                <BaseListItem
                  label="花梗折斷："
                  :loading="status.isDetectorResultLoad"
                  :format-result="formatResult"
                  :modified="status.resultModified"
                  :original-value="detectorCountResult[OBJECT_CLASS_NAME.PEDICEL_BENDING]"
                  :correct-value="formResultModify.correctPedicelBending"
                />
              </b-list-group-item>
              <b-list-group-item>
                <BaseListItem
                  label="葉片受傷："
                  :loading="status.isDetectorResultLoad"
                  :format-result="formatResult"
                  :modified="status.resultModified"
                  :original-value="detectorCountResult[OBJECT_CLASS_NAME.LEAF_INJURED]"
                  :correct-value="formResultModify.correctLeafCrack"
                />
              </b-list-group-item>
            </b-list-group>
          </b-card-group>
          <b-form
            v-show="status.resultCanBeSaved && !!access_token"
            class="mt-3"
          >
            <b-button
              v-b-modal.model-result-modify
              block
              class="mb-3"
              :disabled="status.sendingResult"
              variant="success"
            >
              修改
            </b-button>
            <b-form-group label="批號">
              <b-form-input
                v-model="formResult.batchNumber"
                maxlength="100"
                :disabled="status.sendingResult"
                @keydown.enter.prevent="saveResult"
              />
            </b-form-group>
            <b-button
              block
              class="mt-3 mb-3"
              :disabled="status.sendingResult"
              variant="primary"
              @click="saveResult"
            >
              <b-spinner
                v-show="status.sendingResult"
                small
              >
                這裡的字不會顯示，為了使用 lint 而添加的內容
              </b-spinner>
              {{ result.id ? "更新": "儲存" }}
            </b-button>
          </b-form>
        </b-card>
      </b-col>
    </b-row>
    <!-- 修改結果 -->
    <b-modal
      id="model-result-modify"
      size="sm"
      ok-title="確定"
      cancel-title="取消"
      title="修改綜合結果"
      :scrollable="true"
      @show="onShowResultModify"
      @ok="onOkResultModify"
    >
      <b-form>
        <b-form-group label="綜合結果">
          <b-form-input
            v-model="formResultModify.correctResult"
            type="text"
            plaintext
            disabled
          />
        </b-form-group>

        <b-form-group label="花苞黃化（消蕾）">
          <b-form-input
            v-model="formResultModify.correctBudYellow"
            type="number"
            min="0"
            @change="onChangeResultModify"
          />
        </b-form-group>

        <b-form-group label="掉花（苞）">
          <b-form-input
            v-model="formResultModify.correctDropFlower"
            type="number"
            min="0"
            @change="onChangeResultModify"
          />
        </b-form-group>

        <b-form-group label="花朵受傷">
          <b-form-input
            v-model="formResultModify.correctFlowerInjured"
            type="number"
            min="0"
            @change="onChangeResultModify"
          />
        </b-form-group>

        <b-form-group label="花朵凋萎（黃化）">
          <b-form-input
            v-model="formResultModify.correctFlowerYellow"
            type="number"
            min="0"
            @change="onChangeResultModify"
          />
        </b-form-group>

        <b-form-group label="葉片受傷">
          <b-form-input
            v-model="formResultModify.correctLeafCrack"
            type="number"
            min="0"
            @change="onChangeResultModify"
          />
        </b-form-group>

        <b-form-group label="花梗折斷">
          <b-form-input
            v-model="formResultModify.correctPedicelBending"
            type="number"
            min="0"
            @change="onChangeResultModify"
          />
        </b-form-group>

        <b-form-group label="花朵開放程度">
          <b-form-select
            v-model="formResultModify.correctBloomingDegree"
            :options="optionsBloomingDegree"
          />
        </b-form-group>

        <b-form-group label="全部花朵數">
          <b-form-input
            v-model="formResultModify.correctFlower"
            type="number"
            min="0"
          />
        </b-form-group>

        <b-form-group label="花梗數">
          <b-form-input
            v-model="formResultModify.correctPedicel"
            type="number"
            min="0"
          />
        </b-form-group>
      </b-form>
    </b-modal>
    <b-overlay
      no-wrap
      variant="light"
      :show="status.initLoading"
      rounded="sm"
    >
      <template #overlay>
        <div class="text-center">
          <p id="cancel-label">
            模型載入中...
          </p>
          <b-spinner
            v-show="true"
            variant="success"
            label="Spinning"
          />
        </div>
      </template>
    </b-overlay>
  </b-container>
</template>

<script>
  /**
   * 根據 https://docs.google.com/spreadsheets/d/1Bi6kpo26O8Rbnmj5MC64_l_ygkj6rjB2Alv80cS5EgM/edit#gid=509052460
   * 決定 class 對應那一種處理模型及使用的圖片
   */

  // component
  import Multiselect from 'vue-multiselect'; // 多選 Multiselect 元件導入
  import JSZip from 'jszip';
  import axios from 'axios';
  import BaseImageInput from '../components/BaseImageInput';
  import BaseListItem from '../components/BaseListItem';
  import 'vue-multiselect/dist/vue-multiselect.min.css';

  // plugin
  import mixin from '../lib/mixin.js';

  // 影像種類
  const IMAGE_TYPE = {
    FLOWER: 'flower', // 花梗
    LEAF: 'leaf', // 葉片
  };

  // yolo 物件辨識 class name
  const OBJECT_CLASS_NAME = {
    FLOWER: 'flower', // 花朵數
    FLOWER_BUD: 'flower bud', // 花苞數
    FLOWER_BUD_YELLOW: 'flower bud yellow', // 花苞黃化 (消蕾)
    FLOWER_BUD_DROP: 'flower or bud drop', // 掉花（苞）
    FLOWER_INJURED: 'flower injured', // 花朵受傷
    FLOWER_YELLOW: 'flower yellow', // 花朵萎凋（黃化）
    PEDICEL: 'pedicel', // 花梗數
    PEDICEL_BENDING: 'pedicel bending', // 花梗折斷
    LEAF_INJURED: 'leaf injured', // 葉片受傷
  };

  // 綜合結果等級
  const COMPREHENSIVE_STANDARD_LEVEL = [
    {
      level: 'A',
      max: 0,
      min: 0,
    },
    {
      level: 'B',
      max: 1,
      min: 1,
    },
    {
      level: 'C',
      max: Infinity,
      min: 2,
    },
  ];

  // 預設選取的模型群組
  const DEFAULT_YOLO_MODEL_GROUP = '蝴蝶';

  /**
   * 依特徵，設定是否納入評分的計算
   */
  const YOLO_PROPERTIES = [
    {
      name: OBJECT_CLASS_NAME.FLOWER_BUD_YELLOW,
      computable: true, // 是否納入綜合結果計算
    },
    {
      name: OBJECT_CLASS_NAME.FLOWER_BUD_DROP,
      computable: true,
    },
    {
      name: OBJECT_CLASS_NAME.FLOWER_INJURED,
      computable: true,
    },
    {
      name: OBJECT_CLASS_NAME.FLOWER_YELLOW, // 花朵萎凋（黃化）
      computable: true,
    },
    {
      name: OBJECT_CLASS_NAME.PEDICEL_BENDING, // 花梗折斷
      computable: true,
    },
    {
      name: OBJECT_CLASS_NAME.FLOWER, // 花朵數
      computable: false,
    },
    {
      name: OBJECT_CLASS_NAME.FLOWER_BUD, // 花苞數
      computable: false,
    },
    {
      name: OBJECT_CLASS_NAME.PEDICEL, // 花梗數
      computable: false,
    },
    {
      name: OBJECT_CLASS_NAME.LEAF_INJURED, // 葉片受傷
      computable: true,
    },
  ];

  export default {
    name: 'Home',
    components: {
      BaseImageInput,
      BaseListItem,
      Multiselect,
    },
    filters: {
      formatResultGrade(value) {
        return value || '--';
      },
    },
    mixins: [mixin],
    data() {
      return {
        OBJECT_CLASS_NAME,
        access_token: null, // 呼叫桃改專家系統
        yoloModels: [], // yolo 物件辨識模型

        // 花朵開放程度選單
        optionsBloomingDegree: [
          {
            value: '不滿一成',
            text: '不滿一成',
            check(val) {
              return val < 0.1;
            },
          },
          {
            value: '一成以上',
            text: '一成以上',
            check(val) {
              return val >= 0.1 && val < 0.3;
            },
          },
          {
            value: '三成以上',
            text: '三成以上',
            check(val) {
              return val >= 0.3 && val < 0.5;
            },
          },
          {
            value: '五成以上',
            text: '五成以上',
            check(val) {
              return val >= 0.5 && val < 0.7;
            },
          },
          {
            value: '七成以上',
            text: '七成以上',
            check(val) {
              return val >= 0.7 && val < 0.9;
            },
          },
          {
            value: '九成以上',
            text: '九成以上',
            check(val) {
              return val >= 0.9 && val < 1;
            },
          },
          {
            value: '全開',
            text: '全開',
            check(val) {
              return val >= 1;
            },
          },
        ],

        form: {
          model: {}, // 選擇的物件辨識模型
          imagePedicels: [], // 花梗影像
          imageLeafs: [], // 葉片影像
        },
        detectorResult: {}, // 花卉計算 api 結果

        // 花卉評分結果，儲存功能
        formResult: {
          batchNumber: '',
        },

        // 花卉評分結果，修改
        formResultModify: {
          correctBloomingDegree: '',
          correctFlower: '',
          correctPedicel: '',
          correctResult: '',
          correctBudYellow: '',
          correctDropFlower: '',
          correctFlowerInjured: '',
          correctFlowerYellow: '',
          correctLeafCrack: '',
          correctPedicelBending: '',
        },

        // 狀態的控制
        status: {
          initLoading: true, // 畫面初始化中
          isDetectorResultLoad: false, // 物件辨識處理中
          resultCanBeSaved: false, // 可執行儲存辨識結果
          sendingResult: false, // 發送儲存指令中
          calculating: false, // 計算結果中
          isResponseError: false, // 回應發生錯誤
          resultModified: false, // 綜合結果是否修改過
        },

        // 送到 server 儲存的資料
        result: {
          id: null, // 儲存的 table id
          pictureLink: null, // 下載圖片的路徑
          zipfile: null,
          batchNumber: '',
          models: '',
          bloomingDegree: '',
          flower: '', // 全部花朵數
          pedicel: '',
          result: '',
          budYellow: '',
          dropFlower: '',
          flowerInjured: '',
          flowerYellow: '',
          leafCrack: '',
          pedicelBending: '',
          height: 0, // 株高
          flowerNumber: 0, // 花朵數
          flowerBud: 0, // 花苞數
          // 修正資料不塞預設值
          // correctBloomingDegree: '',
          // correctFlower: '',
          // correctPedicel: '',
          // correctResult: '',
          // correctBudYellow: '',
          // correctDropFlower: '',
          // correctFlowerInjured: '',
          // correctFlowerYellow: '',
          // correctLeafCrack: '',
          // correctPedicelBending: '',
        },
      };
    },
    computed: {
      detectorCountResult() {
        const results = {};
        Object.keys(this.detectorResult).forEach((key) => {
          results[key] = this.detectorResult[key].length;
        });
        return results;
      },
      comprehensiveResult() {
        if (Object.keys(this.detectorCountResult).length === 0) return null;

        // 過濾需要計算結果的特徵
        const classNames = Object.keys(this.detectorCountResult)
          .filter((name) => {
            const property = YOLO_PROPERTIES.find(pp => pp.name === name);
            return property?.computable;
          });

        // 計算數值大於 0 的特徵數量
        const comprehensiveData = classNames.filter((name) => {
          const item = this.detectorCountResult[name];
          return item > 0;
        }).length;

        return comprehensiveData;
      },
      allFlowerCountResult() {
        // 參考 https://redmine.kingkit.codes/issues/1172
        // 全部花朵數 = 花朵數 + 花朵受傷數 + 花朵萎凋 (黃化) + 花苞數 + 花苞黃化 (消蕾) + 掉花苞
        if (Object.keys(this.detectorCountResult).length === 0) return '--';
        const {
          flower,
          [OBJECT_CLASS_NAME.FLOWER_INJURED]: flowerInjured,
          [OBJECT_CLASS_NAME.FLOWER_YELLOW]: flowerYellow,
          [OBJECT_CLASS_NAME.FLOWER_BUD]: flowerBud,
          [OBJECT_CLASS_NAME.FLOWER_BUD_YELLOW]: budYellow,
          [OBJECT_CLASS_NAME.FLOWER_BUD_DROP]: dropFlower,
        } = this.detectorCountResult;
        return flower + flowerInjured + flowerYellow + flowerBud + budYellow + dropFlower;
      },
      bloomingDegree() {
        // 參考 https://redmine.kingkit.codes/issues/3579
        // 開花程度 = ((花朵數 + 花朵受傷數 + 花朵萎凋（黃化）+ 掉花數) / 總花朵數) x 100%
        if (Object.keys(this.detectorCountResult).length === 0
          || this.allFlowerCountResult === '--') return '--';
        const {
          flower,
          [OBJECT_CLASS_NAME.FLOWER_INJURED]: flowerInjured,
          [OBJECT_CLASS_NAME.FLOWER_YELLOW]: flowerYellow,
          [OBJECT_CLASS_NAME.FLOWER_BUD]: flowerBud,
          [OBJECT_CLASS_NAME.FLOWER_BUD_YELLOW]: budYellow,
          [OBJECT_CLASS_NAME.FLOWER_BUD_DROP]: dropFlower,
        } = this.detectorCountResult;

        const value = ((flower + flowerInjured + flowerYellow + dropFlower) / this.allFlowerCountResult);
        const result = this.optionsBloomingDegree.find(degree => {
          return degree.check(value);
        });
        return result.value;
      },
      calculationGrade() {
        if (typeof this.comprehensiveResult !== 'number') return '--';

        const [standard] = COMPREHENSIVE_STANDARD_LEVEL
          .filter(
            item => item.min <= this.comprehensiveResult
              && item.max >= this.comprehensiveResult,
          )
          .map(item => item.level);

        return standard;
      },
      yoloModelOptions() {
        const REG = /__蝴蝶蘭__(.+)__(.+)__(.+)/;
        const options = [];

        function addOption(groupLabel, value) {
          const gg = options.find(obj => obj.groupLabel === groupLabel);
          if (gg) {
            gg.groupValues.push(value);
            gg.groupValues.sort((a, b) => {
              const valA = Number(a.isValid);
              const valB = Number(b.isValid);
              if (valA > valB) return -1;
              return 0;
            });
          } else {
            options.push({
              groupLabel,
              groupItemLabel: `群組：${groupLabel}`,
              groupValues: [value],
            });
          }
        }

        /**
         * 一個群組，只能有 2 個模型，1 個辨識 花，1 個辨識 葉
         * @returns {bolean}
         */
        function isValidOption(groupLabel, { imageType }) {
          const gg = options.find(obj => obj.groupLabel === groupLabel);
          if (!gg) return true;
          const idx = gg.groupValues.findIndex(elem => elem.imageType === imageType);
          return idx === -1;
        }

        function getImageType(label) {
          if (label === '花') return IMAGE_TYPE.FLOWER;
          if (label === '葉') return IMAGE_TYPE.LEAF;
          return null;
        }

        function getModifiedlName(isValid, originalName, imageLabel) {
          if (isValid) return `${originalName}__${imageLabel}`;
          return `( 無效的 ) ${originalName}__${imageLabel}`;
        }

        // 模型群組
        this.yoloModels
          .filter(model => REG.exec(model.name))
          .forEach((model) => {
            const [, groupName, name, imageLabel] = REG.exec(model.name);
            const imageType = getImageType(imageLabel);

            if (!imageType) return;
            const isValid = isValidOption(groupName, { imageType });

            addOption(groupName, {
              id: model.id,
              name: getModifiedlName(isValid, name, imageLabel),
              imageType,
              isValid,
            });
          });

        return options;
      },
    },
    watch: {
      calculationGrade: {
        handler: function readText() {
          if (!this.calculationGrade || this.calculationGrade === '--') return;
          const speechContent = `, 蝴蝶蘭綜合評分為${this.calculationGrade}級`;
          const speechInstance = new SpeechSynthesisUtterance(speechContent);

          speechInstance.lang = 'zh-tw';
          speechInstance.pitch = 0.8;
          speechInstance.rate = 0.8;
          window.speechSynthesis.speak(speechInstance);
        },
      },
      form: {
        handler() {
          // 不可執行儲存
          this.status.resultCanBeSaved = false;
        },
        deep: true,
      },
      yoloModelOptions: {
        handler(options) {
          if (options.length < 1) return;
          const selectedGroup = options.find(option => option.groupLabel === DEFAULT_YOLO_MODEL_GROUP);
          this.form.model = selectedGroup;
        },
      },
    },
    async created() {
      // 載入模型
      this.yoloModels = await this.getYoloModels();

      // 取得 token
      this.access_token = this.getAccessToken();

      this.status.initLoading = false;
    },
    methods: {
      formatResult(value) {
        const isNumber = /[0-9]/;
        return isNumber.test(value) ? value : '--';
      },
      async getYoloModels() {
        try {
          this.status.isResponseError = false;
          const { data: result } = await axios.get(
            `${this.apiPath3}/detectors/`,
            {
              params: {
                hashkey: this.getHash(),
              },
            },
          );
          return result;
        } catch (error) {
          this.status.isResponseError = true;
          console.error(error);
          return null;
        }
      },
      async getImageModels() {
        try {
          const { data } = await axios(`${this.apiPath2}/models`, {
            params: {
              hashkey: this.getHash(),
            },
          });
          return data;
        } catch (err) {
          console.error(err);
          return null;
        }
      },
      getImages(imageType) {
        let images;
        switch (imageType) {
        case IMAGE_TYPE.FLOWER:
          images = this.form.imagePedicels;
          break;

        case IMAGE_TYPE.LEAF:
          images = this.form.imageLeafs;
          break;

        default:
          break;
        }
        return images;
      },
      setFormImagePedicel(files) {
        this.form.imagePedicels.length = 0;
        this.form.imagePedicels.push(...files);
      },
      setFormImageLeaf(files) {
        this.form.imageLeafs.length = 0;
        this.form.imageLeafs.push(...files);
      },
      formValidation() {
        if (!this.form.imagePedicels.length || !this.form.imageLeafs.length) throw Error('請上傳照片資料');
      },
      async calculate() {
        try {
          this.formValidation();

          this.status.calculating = true;
          this.status.isResponseError = false;
          this.status.resultCanBeSaved = false;
          this.status.resultModified = false;

          // 恢復儲存資料的預設值
          this.$data.result = this.$options.data().result;

          await Promise.all([
            // 物件追蹤
            this.yoloIdentify(),
          ]);

          // 可以儲存結果
          this.status.resultCanBeSaved = true;
        } catch (error) {
          console.error(error);
          this.status.isResponseError = true;
        } finally {
          this.status.calculating = false;
        }
      },
      async yoloIdentify() {
        try {
          console.time('yolo identify');
          this.status.isDetectorResultLoad = true;
          this.detectorResult = {};
          this.detectorResult = await this.yoloIdentifyExecute();
        } catch (err) {
          throw err;
        } finally {
          console.timeEnd('yolo identify');
          this.status.isDetectorResultLoad = false;
        }
      },
      /**
       * 處理物件辨識的部份
       * 依照設定，取出模型需要使用的照片，進行辨識
       * @returns {array}
       */
      async yoloIdentifyExecute() {
        const results = [];
        const { groupValues } = this.form.model;
        const validValues = groupValues.filter(value => value.isValid);
        for (const model of validValues) {
          // 依設定，取圖片
          const images = this.getImages(model.imageType);

          for (const image of images) {
            /* eslint-disable-next-line */
            const rt = await this.yoloIdentifyExecuteRequest(model, image);
            results.push(rt);
          }
        }
        return this.yoloIdentifyExecuteResult(results);
      },
      yoloIdentifyExecuteRequest(model, image) {
        return new Promise(async (resolve) => {
          const formData = new FormData();
          formData.append('image', image);
          const { data } = await axios.put(`${this.apiPath3}/detectors/${model.id}/identify`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            params: {
              hashkey: this.getHash(),
            },
          });
          resolve({ data, modelId: parseInt(model.id, 10) });
        });
      },
      /**
       * 整理 yolo 物件辨識結果
       * 數據重複時，取最大值。
       * @returns {object}
       */
      yoloIdentifyExecuteResult(results) {
        const obj = {};

        results.forEach(({ data }) => {
          const { label } = data;
          const classes = Object.keys(label);

          classes.forEach((className) => {
            const previousValue = obj[className];
            const currentValue = label[className];

            if (previousValue) {
              // 重複時，取較大的值
              if (currentValue.length > previousValue.length) {
                obj[className] = currentValue;
              }
            } else {
              obj[className] = currentValue;
            }
          });
        });
        return obj;
      },
      /**
       * 儲存結果
       * 第一次儲存後，之後就只是更新結果，只有當再執行計算時，才會再儲存資料
       */
      async saveResult() {
        if (!this.access_token) return;

        const objectModels = (ids) => {
          // 物件辨識使用套件，選單內容是使用模型的完整資料，沒有另外做選單的處理，所以使用 model.id 做過濾條件
          const models = this.yoloModels.filter(model => ids.includes(model.id));
          return models.map(model => ({
            id: model.id,
            name: model.name,
            type: 'yolo',
          }));
        };

        this.status.sendingResult = true;

        let zipfile = new JSZip();

        // 花梗影像
        this.form.imagePedicels.forEach((image) => {
          zipfile.file(image.name || Date.now(), image, { base64: true });
        });

        // 葉片影像
        this.form.imageLeafs.forEach((image) => {
          zipfile.file(image.name || Date.now(), image, { base64: true });
        });

        // blob to file
        zipfile = await zipfile.generateAsync({ type: 'blob' });
        zipfile = new File([zipfile], `${Date.now().zip}`);

        // 整理使用的模型
        const ids = this.form.model.groupValues.reduce((acc, model) => {
          acc.push(model.id);
          return acc;
        }, []);
        const models = objectModels(ids);

        // class name
        const {
          FLOWER,
          FLOWER_BUD,
          FLOWER_BUD_YELLOW,
          FLOWER_BUD_DROP,
          FLOWER_INJURED,
          FLOWER_YELLOW,
          LEAF_INJURED,
          PEDICEL_BENDING,
        } = OBJECT_CLASS_NAME;

        Object.assign(this.result, {
          zipfile,
          batchNumber: this.formResult.batchNumber,
          models: JSON.stringify(models),
          bloomingDegree: this.bloomingDegree,
          flower: this.allFlowerCountResult,
          pedicel: this.detectorCountResult.pedicel,
          result: this.calculationGrade,
          budYellow: this.detectorCountResult[FLOWER_BUD_YELLOW],
          dropFlower: this.detectorCountResult[FLOWER_BUD_DROP],
          flowerInjured: this.detectorCountResult[FLOWER_INJURED],
          flowerYellow: this.detectorCountResult[FLOWER_YELLOW],
          leafCrack: this.detectorCountResult[LEAF_INJURED],
          pedicelBending: this.detectorCountResult[PEDICEL_BENDING],
          flowerNumber: this.detectorCountResult[FLOWER],
          flowerBud: this.detectorCountResult[FLOWER_BUD],
        });

        if (this.status.resultModified) {
          Object.assign(this.result, this.formResultModify);
        }

        const form = new FormData();
        // eslint-disable-next-line guard-for-in
        for (const key in this.result) {
          const value = this.result[key];
          if (value !== null && value !== undefined) form.append(key, value);
        }

        try {
          const { data: { instance: result } } = await axios.put(
            `${this.taoyuanExpertHost()}/api/flower_phalaenopses/uploadResult`,
            form,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: this.access_token,
              },
            },
          );

          // 結果中有 id 時，就會更新該筆資料
          this.result.id = result.id;
          this.result.pictureLink = result.pictureLink;
        } catch (error) {
          console.error(error);
        }
        this.status.sendingResult = false;
      },
      // 同步資料到修改結果的 modal
      syncToFormResultModify() {
        const {
          FLOWER_BUD_YELLOW,
          FLOWER_BUD_DROP,
          FLOWER_INJURED,
          FLOWER_YELLOW,
          LEAF_INJURED,
          PEDICEL_BENDING,
        } = OBJECT_CLASS_NAME;
        this.formResultModify.correctResult = this.calculationGrade;
        this.formResultModify.correctBudYellow = this.detectorCountResult[FLOWER_BUD_YELLOW];
        this.formResultModify.correctDropFlower = this.detectorCountResult[FLOWER_BUD_DROP];
        this.formResultModify.correctFlowerInjured = this.detectorCountResult[FLOWER_INJURED];
        this.formResultModify.correctFlowerYellow = this.detectorCountResult[FLOWER_YELLOW];
        this.formResultModify.correctLeafCrack = this.detectorCountResult[LEAF_INJURED];
        this.formResultModify.correctPedicelBending = this.detectorCountResult[PEDICEL_BENDING];
        this.formResultModify.correctBloomingDegree = this.bloomingDegree;
        this.formResultModify.correctFlower = this.allFlowerCountResult;
        this.formResultModify.correctPedicel = this.detectorCountResult.pedicel;
      },
      onShowResultModify() {
        if (this.status.resultModified) return;
        this.syncToFormResultModify();
      },
      onOkResultModify() {
        this.status.resultModified = true;
      },
      onChangeResultModify() {
        // 不需要納入計算的特徵
        const excludeProperties = [
          'correctResult',
          'correctBloomingDegree',
          'correctFlower',
          'correctPedicel',
        ];

        // 計算數值大於 0 的特徵數量
        const correctComprehensiveData = Object.keys(this.formResultModify)
          .filter(key => !excludeProperties.includes(key))
          .reduce((acc, currentKey) => {
            const value = parseInt(this.formResultModify[currentKey], 10);
            if (value > 0) return acc + 1;
            return acc;
          }, 0);

        // 計算綜合結果
        const [standard] = COMPREHENSIVE_STANDARD_LEVEL
          .filter(
            item => item.min <= correctComprehensiveData
              && item.max >= correctComprehensiveData,
          )
          .map(item => item.level);

        this.formResultModify.correctResult = standard;
      },
    },
  };
</script>
